import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AddressService } from 'core/address/address.service';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { ExportUtilsService } from 'core/export-utils/export-utils.service';
import { Address } from '../../shared/entities/address.class';
import { ContactData } from '../../shared/entities/contact-data.class';

@Injectable({
  providedIn: 'root'
})
export class LeadsService {
  private urls = {
    create: '/api/lead/create',
    createDeliveryLocation: '/api/delivery/create',
    accountManagerList: '/api/accountmanager/list',
    getDeliveryLocationDetails: '/api/delivery/get/',
    getLeadDetails: '/api/lead/get/',
    editLead: '/api/lead/edit',
    editDeliveryLocation: '/api/delivery/edit',
    getLeadsAndDeliveryLocations: '/api/lead/leadsByAccountManager/',
    getAccounts: '/api/customer/byAccountManager/',
    getCompanyDetails: '/api/customer/customerCountry/',
    mergeLeads: '/api/lead/merge',
    rejectLead: '/api/lead/reject',
    downloadCSVFile: '/api/lead/download-template',
    uploadCSVFile: '/api/lead/upload-template',
  };

  constructor(
    private apiService: ApiService,
    private appInfoService: AppInfoService,
    private addressService: AddressService,
    private exportUtilsService: ExportUtilsService
  ) {
  }

  /**
   * Gets static template values
   * @param callback - The callback
   */
  getAccountManagerList(callback: (...args: any[]) => any) {
    this.apiService.get(this.urls.accountManagerList)
      .subscribe(result => callback(result));
  }


  getCompanyDetails(companyId, callback: (...args: any[]) => any) {
    return this.apiService.get(this.urls.getCompanyDetails + companyId).subscribe(value => callback(value));
  }


  getDeliveryLocationDetails(deliveryId, callback: (...args: any[]) => any) {
    return this.apiService.get(this.urls.getDeliveryLocationDetails + deliveryId).subscribe(value => callback(value));
  }

  getLeadDetails(leadId, callback: (...args: any[]) => any) {
    return this.apiService.get(this.urls.getLeadDetails + leadId).subscribe(value => callback(value));
  }

  getLeadsAndAccountsByAccountManager(amId, callback: (...args: any[]) => any) {
    forkJoin([
      this.apiService.get(this.urls.getLeadsAndDeliveryLocations + amId),
      this.apiService.get(this.urls.getAccounts + amId)]
    ).subscribe(value => callback(value));
  }

  /**
   * Get leads by Account Manager Id
   * @param amId - account manager Id
   * @param callback - callback
   */
  getLeadsByAccountManager(amId: string, callback: (...args: any[]) => any) {
    this.apiService.get(this.urls.getLeadsAndDeliveryLocations + amId).subscribe(value => callback(value));
  }

  mergeLeads(customerId, leadId) {
    const payload = {
      customerId, leadId
    };
    return this.apiService.post(this.urls.mergeLeads, payload);
  }

  rejectLead(leadId, rejectReason) {
    const payload = {
      leadId, rejectReason
    };
    return this.apiService.post(this.urls.rejectLead, payload);
  }

  private generateLeadPayloadObject(leadData, clientData?, id?, leadType?, leadStatus?) {
    const payload: any = {};
    payload.id = id || this.appInfoService.emptyGuid;
    payload.accountManagerId = leadData.accountManager;
    payload.sourceTypeId = leadData.sourceTypeId;
    payload.company = {
      id: leadData.company.id || this.appInfoService.emptyGuid,
      name: (leadData.company.name1 || leadData.company.name) || leadData.company
    };
    payload.leadType = leadType;
    payload.leadStatus = leadStatus;
    payload.address = this.packLeadProfileData(leadData.address);
    payload.deleteReason = '';

    if (clientData) {
      payload.contacts = [this.packLeadContactData(clientData)];
    } else {
      payload.contacts = null;
    }

    return payload;
  }

  private generateDeliveryPayloadObject(leadData, id?, leadType?, leadStatus?) { // For create and edit Delivery Location
    const payload: any = {};
    payload.id = id || this.appInfoService.emptyGuid;
    payload.accountManagerId = leadData.accountManager;
    payload.customerId = leadData.company;
    payload.leadType = leadType;
    payload.leadStatus = leadStatus || null;
    payload.address = this.packProfileAddressDataForDelivery(leadData.address); // packProfileData
    payload.deleteReason = '';

    return payload;
  }

  private packLeadProfileData(addressValues) {
    const address: Address = addressValues;
    this.addressService.normalizeAddressStateAndZip(address);
    return address;
  }

  private packLeadContactData(clientData) {
    const contact = new ContactData();

    contact.address = clientData.address;
    this.addressService.normalizeAddressStateAndZip(contact.address);

    contact.notes = clientData.notes || [];
    contact.id = clientData.id;
    contact.firstName = clientData.firstName;
    contact.middleName = clientData.middleName || '';
    contact.lastName = clientData.lastName || '';
    contact.suffix = clientData.suffix || '';
    contact.department = clientData.department || '';
    contact.title = clientData.title || '';
    contact.sameAddressAsLead = clientData.addressToggleCheckbox;
    contact.email = clientData.email || '';
    contact.phones = clientData.phones;

    return contact;
  }

  private packProfileAddressDataForDelivery(profileValues) {
    const payload: Address = profileValues;
    this.addressService.normalizeAddressStateAndZip(payload);

    return payload;
  }

  createContact(leadData, clientData?, id?, leadType?, leadStatus?,
    callback?: (...args: any[]) => any, errorCallback?: (...args: any[]) => any) {
    this.createLead(this.generateLeadPayloadObject(leadData, clientData, id, leadType, leadStatus), callback, errorCallback);
  }

  updateLead(leadData, id?, leadType?, leadStatus?, callback?: (...args: any[]) => any) {
    this.editLead(this.generateLeadPayloadObject(leadData, null, id, leadType, leadStatus), callback);
  }

  updateDeliveryLocation(deliveryData, id?, leadType?, leadStatus?, callback?: (...args: any[]) => any) {
    this.editDeliveryLocation(this.generateDeliveryPayloadObject(deliveryData, id, leadType, leadStatus), callback);
  }

  createDeliveryLocation(deliveryData, id?, leadType?, leadStatus?, callback?: (...args: any[]) => any) {
    this.generateDeliveryLocation(this.generateDeliveryPayloadObject(deliveryData, id, leadType, leadStatus), callback);
  }

  private createLead(payload, callback, errorCallback?: (...args: any[]) => any) {
    return this.postAndCheckForCallback(payload, this.urls.create, callback, errorCallback);
  }

  private editLead(payload, callback) {
    return this.postAndCheckForCallback(payload, this.urls.editLead, callback);
  }

  private editDeliveryLocation(payload, callback) {
    return this.postAndCheckForCallback(payload, this.urls.editDeliveryLocation, callback);
  }

  private generateDeliveryLocation(payload, callback) {
    return this.postAndCheckForCallback(payload, this.urls.createDeliveryLocation, callback);
  }

  private postAndCheckForCallback(payload, url: string, callback, errorCallback?) {
    return this.apiService.post(url, payload)
      .subscribe(() => {
        if (callback) {
          callback();
        }
      }, () => {
        if (errorCallback) {
          errorCallback();
        }
      });
  }

  /**
   * Downloads the Excel Template
   */
  downloadExcelTemplate() {
    this.apiService.getBlob(this.urls.downloadCSVFile)
      .subscribe((data: ArrayBuffer) => this.exportUtilsService.downloadFile(data, 'LEADS.csv'));
  }

  /**
   * Uploads the Excel file
   * @param fileList - The list of files
   * @param accountManagerId - account manager Id
   * @param callback - A callback to be triggered when upload is done
   */
  uploadExcel(fileList: FileList, accountManagerId: string, callback: (...args: any[]) => any) {
    const file: File = fileList[0];
    const formData: FormData = new FormData();
    formData.append('LeadsUploadTemplate', file, file.name);
    formData.append('accountManagerId', accountManagerId);

    return this.apiService.post(this.urls.uploadCSVFile, formData)
      .subscribe(() => callback(), error => callback(error));
  }
}
